import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEntitlementGate } from '../contexts/EntitlementGateContext';
import { useAnalytics } from '../hooks/useAnalytics';
import { useSearch } from '../contexts/SearchContext';
import { useTutorial } from '../contexts/TutorialContext';
import { useAPI } from '../contexts/APIContext';
import { useProfile } from '../contexts/ProfileContext';
import PixleyStarsModal from './PixleyStarsModal';
import SubscriptionModal from './SubscriptionModal';

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const Navigation = () => {
  const { isAuthenticated, user, signOut, subscriptionStatus, hasEntitlement } = useAuth();
  const { requireEntitlement, openWall, cutoverLive } = useEntitlementGate();
  const { profiles, currentProfile, switchProfile, requireParentalVerification } = useProfile();
  const { currentTutorialStep } = useTutorial();
  const { trackUpgradeButtonClicked } = useAnalytics();
  const { apiClient } = useAPI();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isStarsModalOpen, setIsStarsModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const { searchQuery, setSearchQuery, isSearching, handleSearch, handleClear } = useSearch();
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [isLightBg, setIsLightBg] = useState(false);
  const [unauthNavFull, setUnauthNavFull] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const profileMenuRef = useRef(null);

  // Logged-out desktop (lg+): start centered on Pixley logo, then reveal full navbar
  useLayoutEffect(() => {
    if (isAuthenticated) {
      setUnauthNavFull(true);
      return;
    }
    if (typeof window === "undefined") return;
    if (window.matchMedia("(max-width: 1023px)").matches) {
      setUnauthNavFull(true);
      return;
    }
    setUnauthNavFull(false);
  }, [isAuthenticated]);

  // Launch-day cutover explainer: shown ONCE to existing signed-in users
  // without an entitlement when the members-only model goes live. New users
  // are excluded by the post-onboarding flag path above.
  useEffect(() => {
    if (!cutoverLive || !isAuthenticated || hasEntitlement) return;
    if (!subscriptionStatus?.isLoaded) return;
    try {
      if (localStorage.getItem('pixley_cutover_seen_v1')) return;
      localStorage.setItem('pixley_cutover_seen_v1', 'true');
      openWall({ trigger: "launch_explainer" });
    } catch {
      // localStorage unavailable — skip rather than loop
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cutoverLive, isAuthenticated, hasEntitlement, subscriptionStatus?.isLoaded]);

  useEffect(() => {
    if (isAuthenticated) return;
    if (typeof window === "undefined") return;
    if (window.matchMedia("(max-width: 1023px)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setUnauthNavFull(true);
      return;
    }
    // Hold logo-only longer so the hero can establish before the bar expands
    const id = window.setTimeout(() => setUnauthNavFull(true), 1400);
    return () => window.clearTimeout(id);
  }, [isAuthenticated]);

  const handleSignOut = async () => {
    const executeSignOut = async () => {
      await signOut();
      setIsMenuOpen(false);
      setIsProfileMenuOpen(false);
      navigate('/');
    };

    requireParentalVerification('sign out', executeSignOut);
  };

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  const handleAuthNavigation = () => {
    navigate('/auth');
    setIsMenuOpen(false);
  };

  // Show tier badge for all authenticated users
  const showTierBadge = isAuthenticated && subscriptionStatus?.isLoaded;
  const isFreeUser = subscriptionStatus?.subscriptionType === "NONE";

  const handleUpgradeClick = () => {
    // Track upgrade button click
    trackUpgradeButtonClicked('navigation');

    navigate('/settings?tab=stats'); // Navigate to subscription tab
    setIsMenuOpen(false);
  };


  // Star chip: frozen (lock + wall) for unentitled users under the cutover.
  const starsAreFrozen = cutoverLive && isAuthenticated && !hasEntitlement;
  const handleStarChipClick = () => {
    if (starsAreFrozen) {
      openWall({ trigger: "stars" });
      return;
    }
    setIsStarsModalOpen(true);
  };

  const handlePremiumBadgeClick = () => {
    // Under the members-only cutover, unentitled users get the TrialWall;
    // entitled users keep the plan-management modal.
    if (cutoverLive && !hasEntitlement) {
      openWall({ trigger: "post_onboarding" });
      return;
    }
    requireParentalVerification('view subscription options', () => {
      setIsSubscriptionModalOpen(true);
    });
  };

  // Get display name for subscription tier
  const getTierDisplayName = (tier) => {
    const tierMap = {
      'NONE': 'Free',
      'STARTER': 'Starter',
      'PLUS': 'Pro',
      'PRO': 'Pro',
      'FAMILY': 'Family',
      'UNLIMITED': 'Max',
      // Legacy tiers
      'PREMIUM': 'Pro',
      'EDDY': 'Pro',
      'PHILIP': 'Family',
      'NIKHIL': 'Max',
    };
    return tierMap[tier] || 'Premium';
  };

  const currentTierName = getTierDisplayName(subscriptionStatus?.subscriptionType);

  // Debug logging
  console.log('🔍 Subscription Debug:', {
    subscriptionType: subscriptionStatus?.subscriptionType,
    displayName: currentTierName,
    fullStatus: subscriptionStatus
  });

  const navItems = [
    { path: '/storyverse', label: 'Storyverse' },
    { path: '/create', label: 'Create' },
    { path: '/library', label: 'Library' },
  ];

  const isStoriversePage = location.pathname === '/storyverse';
  const isAboutPage = location.pathname === '/about';
  const isLandingPage = location.pathname === '/' || isAboutPage;
  const isBlogPage = location.pathname.startsWith('/blog');
  // The gift page keeps the navbar but lets it scroll away with the page.
  const isGiftPage = location.pathname === '/gift' || location.pathname === '/redeem';
  // pages where the navbar starts flush at the top and only picks up its
  // floating pill once scrolled (storyverse keeps the solid navbar always —
  // transparent links over the hero art were unreadable)
  const isMergePage = isLandingPage || isBlogPage;

  useEffect(() => {
    if (!isMergePage) return;
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isMergePage]);

  const navMerged = isMergePage && !scrolled && !isMenuOpen;
  // over a dark video/image hero (landing, storyverse) text is white; over
  // light blog headers it stays dark
  const mergedDark = navMerged && isLandingPage;
  const mergedLight = navMerged && isBlogPage;
  const shouldTriggerTutorialAfterCtaCloseRef = useRef(false);

  // Clear search on every route change — no stale search across pages
  useEffect(() => {
    setLocalSearchQuery('');
    setSearchQuery('');
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync local state with context when context changes externally
  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  // Post-onboarding subscription CTA trigger. Waits for subscriptionStatus
  // to LOAD before deciding — firing on isAuthenticated alone raced the
  // status fetch, read cutoverLive=false, and burned the flag on the legacy
  // modal path (users walked straight into the app with no wall). Under the
  // cutover the flag is kept until the user is actually entitled, so the
  // mandatory wall reappears on reload until they start a trial.
  const postOnboardingWallShownRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !subscriptionStatus?.isLoaded) return;
    try {
      const showCTA = localStorage.getItem('pixley_show_subscription_cta');
      if (showCTA !== 'true') return;
      if (cutoverLive && !hasEntitlement) {
        shouldTriggerTutorialAfterCtaCloseRef.current = true;
        if (!postOnboardingWallShownRef.current) {
          postOnboardingWallShownRef.current = true;
          openWall({ trigger: "post_onboarding" });
        }
        // Flag intentionally NOT removed — cleared only once entitled.
      } else if (cutoverLive && hasEntitlement) {
        // Trial started (e.g. just back from checkout): consume the flag and
        // hand over to the tutorial.
        localStorage.removeItem('pixley_show_subscription_cta');
        localStorage.setItem('pixley_onboarding_just_completed', 'true');
      } else {
        // Pre-cutover behavior: one-shot legacy modal.
        shouldTriggerTutorialAfterCtaCloseRef.current = true;
        setIsSubscriptionModalOpen(true);
        localStorage.removeItem('pixley_show_subscription_cta');
      }
    } catch {
      // Ignore localStorage access failures
    }
  }, [isAuthenticated, subscriptionStatus?.isLoaded, cutoverLive, hasEntitlement]);

  const handleCloseSubscriptionModal = () => {
    setIsSubscriptionModalOpen(false);

    // Only trigger tutorial when this modal was auto-opened post-onboarding.
    if (!shouldTriggerTutorialAfterCtaCloseRef.current) return;
    shouldTriggerTutorialAfterCtaCloseRef.current = false;

    try {
      localStorage.setItem('pixley_onboarding_just_completed', 'true');
    } catch {
      // Ignore localStorage access failures
    }

    // Reload so TutorialContext picks up onboarding completion flag.
    setTimeout(() => {
      window.location.reload();
    }, 150);
  };

  // Keep the global search query in sync with what's typed.
  useEffect(() => {
    setSearchQuery(localSearchQuery);
  }, [localSearchQuery, setSearchQuery]);

  // Live (as-you-type) search: debounce the query so results update without
  // pressing Enter. The search bar only renders on Storyverse, so this never
  // needs to navigate. Enter/the Go button still trigger an immediate search.
  useEffect(() => {
    if (!isStoriversePage) return;
    const t = setTimeout(() => {
      if (localSearchQuery.trim()) {
        handleSearch(localSearchQuery);
      } else {
        handleClear();
      }
    }, 350);
    return () => clearTimeout(t);
  }, [localSearchQuery, isStoriversePage, handleSearch, handleClear]);

  const handleClearSearch = () => {
    setLocalSearchQuery('');
    handleClear();
  };

  const handleSearchWithNavigation = () => {
    const queryToSearch = localSearchQuery;
    if (!queryToSearch.trim()) return;

    // Sync context immediately so Storyverse UI shows the search state
    setSearchQuery(queryToSearch);

    // If not on Storyverse page, navigate first then search once callbacks register
    if (!isStoriversePage) {
      navigate('/storyverse');
      // Wait for Storyverse to mount and register search callbacks
      requestAnimationFrame(() => {
        setTimeout(() => {
          handleSearch(queryToSearch);
        }, 350);
      });
      return;
    }

    handleSearch(queryToSearch);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearchWithNavigation();
    }
  };

  // Smart background-based theme switching - ONLY on About page
  useEffect(() => {
    // On About page, set white background. On all other pages, keep dark (normal)
    if (isAboutPage) {
      setIsLightBg(true); // Force white on About page
    } else {
      setIsLightBg(false); // Keep dark on all other pages
      return; // Don't run background detection on other pages
    }

    let timeout;
    const detectBackground = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        try {
          // Detect element directly behind the navbar (center top position)
          const centerX = window.innerWidth / 2;
          const topY = 10; // Small offset from top
          const el = document.elementFromPoint(centerX, topY);
          
          if (!el) {
            // Fallback: try to get body or html element
            const bodyEl = document.body || document.documentElement;
            if (!bodyEl) return;
            
            const bg = window.getComputedStyle(bodyEl).backgroundColor;
            const rgb = bg.match(/\d+/g);
            
            if (rgb && rgb.length >= 3) {
              const [r, g, b] = rgb.map(Number);
              const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
              setIsLightBg(luminance > 180);
            }
            return;
          }

          // Get computed background color
          let bg = window.getComputedStyle(el).backgroundColor;
          
          // If background is transparent, traverse up the DOM tree
          let currentEl = el;
          let depth = 0;
          while ((!bg || bg === 'rgba(0, 0, 0, 0)' || bg === 'transparent') && depth < 5) {
            currentEl = currentEl.parentElement;
            if (!currentEl || currentEl === document.body || currentEl === document.documentElement) {
              bg = window.getComputedStyle(document.body).backgroundColor;
              break;
            }
            bg = window.getComputedStyle(currentEl).backgroundColor;
            depth++;
          }

          // Parse RGB values
          const rgb = bg.match(/\d+/g);
          
          if (rgb && rgb.length >= 3) {
            const [r, g, b] = rgb.map(Number);
            const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
            setIsLightBg(luminance > 180);
          }
        } catch (error) {
          // Silently fail - keep current state
          console.debug('Background detection error:', error);
        }
      }, 150);
    };

    // Initial detection with a small delay to prevent glitches during navigation
    const initialDelay = setTimeout(() => {
      detectBackground();
    }, 100);

    // Listen to scroll and resize events
    window.addEventListener('scroll', detectBackground, { passive: true });
    window.addEventListener('resize', detectBackground);

    return () => {
      clearTimeout(timeout);
      clearTimeout(initialDelay);
      window.removeEventListener('scroll', detectBackground);
      window.removeEventListener('resize', detectBackground);
    };
  }, [location.pathname, isAboutPage]); // Re-detect when route changes

  // Text color classes - always light theme now (Pixley redesign)
  const textPrimary = 'text-[#0B1A33]';
  const textSecondary = 'text-[#334155]';
  // Over the dark video hero links go white; everywhere else they stay dark
  const navLinkClasses = mergedDark
    ? 'text-white hover:text-white/80 hover:bg-white/10'
    : 'text-[#334155] hover:text-[#0065F8] hover:bg-[#0065F8]/8';
  const textTertiary = 'text-[#334155]/60';
  const hoverText = 'hover:text-[#0065F8]';
  const hoverBg = 'hover:bg-[#0065F8]/8';
  const borderColor = 'border-[rgba(11,26,51,0.08)]';

  return (
    <nav
      className={`${isGiftPage ? 'absolute' : 'fixed'} left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-in-out ${
        navMerged ? '' : 'backdrop-blur-md'
      } ${isMenuOpen ? 'rounded-2xl' : navMerged ? 'rounded-none' : 'rounded-3xl'}`}
      style={{
        top: navMerged ? '10px' : isStoriversePage ? '12px' : '24px',
        width: '100%',
        maxWidth: navMerged ? '100%' : showTierBadge ? '1400px' : '1200px',
        background: navMerged ? 'transparent' : 'rgba(255,253,247,0.85)',
        border: navMerged ? '2px solid transparent' : '2px solid rgba(11,26,51,0.06)',
        boxShadow: navMerged ? 'none' : '0px 4px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.08)',
        color: '#0B1A33',
      }}
    >
      <div className={`px-4 sm:px-6 lg:px-8 ${showTierBadge ? 'lg:px-10' : ''}`}>
        <div className="flex items-center h-16 justify-between w-full">
          {isAuthenticated ? (
            <>
              {/* LEFT: Pixley logo (for authenticated) */}
              <div className="flex items-center gap-4 flex-shrink-0">
                  <button
                    onClick={() => ![9, 11, 12].includes(currentTutorialStep) && handleNavigation('/storyverse')}
                    disabled={[9, 11, 12].includes(currentTutorialStep)}
                    className={`transition-all duration-200 focus:outline-none rounded-lg px-1 py-1 ${
                      [9, 11, 12].includes(currentTutorialStep) ? 'opacity-30 cursor-not-allowed' : ''
                    }`}
                  >
                    <img src="/assets/pixley-logo-full.png" alt="Pixley" style={{ height: 56, objectFit: "contain" }} />
                  </button>
              </div>
            </>
                ) : (
            <>
              {/* Mobile / tablet: full left cluster (unchanged) */}
              <div className="flex items-center gap-4 flex-shrink-0 lg:hidden">
                <button
                  onClick={() => handleNavigation('/')}
                  className="transition-all duration-200 focus:outline-none rounded-lg px-1 py-1"
                >
                  <img src="/assets/pixley-logo-full.png" alt="Pixley" style={{ height: 56, objectFit: "contain" }} />
                </button>
              </div>

              {/* Desktop lg+: start centered on logo, spring open to full bar */}
              <motion.div
                className="hidden lg:flex flex-1 w-full min-w-0 items-center"
                initial={false}
                animate={{
                  justifyContent: unauthNavFull ? 'space-between' : 'center',
                }}
                transition={{ type: 'spring', stiffness: 280, damping: 32, mass: 0.95 }}
              >
                <motion.div
                  layout
                  className="flex items-center gap-4 min-w-0"
                  transition={{ layout: { type: 'spring', stiffness: 300, damping: 32 } }}
                >
                  <motion.button
                    layout
                    type="button"
                    onClick={() => handleNavigation('/')}
                    className="transition-all duration-200 focus:outline-none rounded-lg px-1 py-1"
                    transition={{ layout: { type: 'spring', stiffness: 320, damping: 30 } }}
                  >
                    <img src="/assets/pixley-logo-full.png" alt="Pixley" style={{ height: 56, objectFit: "contain" }} />
                  </motion.button>
                </motion.div>

                {unauthNavFull && (
                  <>
                    <div
                      className="absolute left-1/2 -translate-x-1/2 flex items-center"
                      style={{
                        background: mergedDark ? "rgba(255,255,255,0.14)" : "rgba(0,101,248,0.05)",
                        border: mergedDark ? "1.5px solid rgba(255,255,255,0.28)" : "1.5px solid rgba(11,26,51,0.08)",
                        backdropFilter: "blur(10px)",
                        WebkitBackdropFilter: "blur(10px)",
                        borderRadius: 999,
                        padding: 4,
                        gap: 2,
                        transition: "background 300ms ease, border-color 300ms ease",
                      }}
                    >
                      {[
                        {
                          label: "Storyverse",
                          path: "/storyverse",
                          icon: (
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flexShrink: 0, display: "block" }}>
                              <rect x="2.5" y="7.5" width="19" height="13" rx="3" />
                              <path d="M7.5 3l4.5 4.5L16.5 3" />
                              <path d="M10.5 12.2l3.2 1.8-3.2 1.8v-3.6z" fill="currentColor" stroke="none" />
                            </svg>
                          ),
                        },
                        {
                          label: "Blog",
                          path: "/blog",
                          icon: (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flexShrink: 0, display: "block" }}>
                              <path d="M12 20h9" />
                              <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                            </svg>
                          ),
                        },
                      ].map(({ label, path, icon }) => (
                        <button
                          key={path}
                          type="button"
                          onClick={() => handleNavigation(path)}
                          className={`flex items-center gap-1.5 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 ${
                            mergedDark
                              ? "text-white hover:bg-white/20 focus:ring-white/30"
                              : "text-[#0B1A33] hover:text-[#0065F8] hover:bg-[#0065F8]/10 focus:ring-[#0065F8]/20"
                          }`}
                          style={{
                            fontFamily: "'Fredoka', sans-serif",
                            fontWeight: 600,
                            fontSize: 13.5,
                            letterSpacing: "0.02em",
                            padding: "7px 16px",
                          }}
                        >
                          {icon}
                          {label}
                        </button>
                      ))}
                    </div>
                    <div className="flex-1 min-w-[1rem]" aria-hidden />
                    <motion.div
                      className="flex items-center space-x-3 flex-shrink-0"
                      initial={{ opacity: 0, x: 8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 320,
                        damping: 28,
                        delay: 0.06,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => navigate('/auth?mode=login')}
                        className="relative rounded-full px-6 py-2.5 text-sm font-bold transition-all duration-150 focus:outline-none"
                        style={{ background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)", color: "#FFF", boxShadow: "0px 3px 0px #073B8E" }}
                      >
                        Log In
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate('/auth?mode=signup')}
                        className="relative rounded-full px-6 py-2.5 text-sm font-bold transition-all duration-150 focus:outline-none"
                        style={{ background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)", color: "#FFF", boxShadow: "0px 3px 0px #073B8E" }}
                      >
                        Sign Up
                      </button>
                    </motion.div>
                  </>
                )}
              </motion.div>
            </>
          )}

          {/* CENTER: Desktop Navigation (only when authenticated) */}
          {isAuthenticated && (
            <div className={`hidden lg:flex items-center flex-1 justify-center ${showTierBadge ? 'space-x-1.5' : 'space-x-2'}`}>
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => ![9, 11, 12].includes(currentTutorialStep) && handleNavigation(item.path)}
                  disabled={[9, 11, 12].includes(currentTutorialStep)}
                  className={`px-4 py-2.5 rounded-full transition-all duration-150 text-sm font-semibold focus:outline-none ${
                    [9, 11, 12].includes(currentTutorialStep)
                      ? 'opacity-30 cursor-not-allowed'
                      : isActive(item.path)
                      ? 'bg-[#0065F8] text-white shadow-[0px_3px_0px_#073B8E]'
                      : 'text-[#334155] hover:text-[#0065F8] hover:bg-[rgba(0,101,248,0.06)]'
                  }`}
                >
                  <span>{item.label}</span>
                </button>
              ))}

              {/* Games Button */}
              <button
                onClick={() => ![9, 11, 12].includes(currentTutorialStep) && handleNavigation('/games')}
                disabled={[9, 11, 12].includes(currentTutorialStep)}
                className={`px-4 py-2.5 rounded-full transition-all duration-150 text-sm font-semibold focus:outline-none ${
                  [9, 11, 12].includes(currentTutorialStep)
                    ? 'opacity-30 cursor-not-allowed'
                    : isActive('/games')
                    ? 'bg-[#0065F8] text-white shadow-[0px_3px_0px_#073B8E]'
                    : 'text-[#334155] hover:text-[#0065F8] hover:bg-[rgba(0,101,248,0.06)]'
                }`}
              >
                <span>Games</span>
              </button>

              {/* Search Bar - Visible only on Storyverse page when authenticated */}
              {isAuthenticated && isStoriversePage && (
                <div
                  className={`flex items-center backdrop-blur-md rounded-full px-3 py-2 ml-4 transition-all duration-300 ease-in-out focus-within:ring-2 ${
                    isAboutPage
                      ? 'bg-black/10 border border-black/20 hover:bg-black/15 focus-within:ring-black/30'
                      : 'bg-white/10 border border-white/20 hover:bg-white/15 focus-within:ring-white/30'
                  }`}
                  style={{ minWidth: '280px' }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={'rgba(0, 0, 0, 0.6)'}
                    strokeWidth="2"
                    className="flex-shrink-0"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>

                  <input
                    type="text"
                    placeholder="Search stories..."
                    value={localSearchQuery}
                    onChange={(e) => { setLocalSearchQuery(e.target.value); }}
                    onKeyDown={handleSearchKeyDown}
                    className={`flex-1 bg-transparent border-none text-sm ${textPrimary} placeholder-black/50 outline-none px-3`}
                  />

                  {localSearchQuery && (
                    <button
                      onClick={handleClearSearch}
                      className={"text-[#334155]/50 hover:text-[#0065F8] transition-colors duration-150 flex-shrink-0 px-1"}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  )}

                  <button
                    onClick={handleSearchWithNavigation}
                    disabled={!localSearchQuery.trim()}
                    className="bg-[#0065F8] hover:bg-[#0055D6] disabled:bg-[#D7E3F3] disabled:text-[#8CA0BC] disabled:cursor-not-allowed text-white text-xs font-bold px-3 py-1 rounded-full ml-2 transition-all duration-150 flex-shrink-0"
                  >
                    Go
                  </button>
                </div>
              )}

              {/* Weekly Challenge Button */}
              <button
                onClick={() => ![9, 11, 12].includes(currentTutorialStep) && handleNavigation('/weekly-challenge')}
                disabled={[9, 11, 12].includes(currentTutorialStep)}
                className={`px-4 py-2.5 rounded-full transition-all duration-150 text-sm font-semibold focus:outline-none ${
                  [9, 11, 12].includes(currentTutorialStep)
                    ? 'opacity-30 cursor-not-allowed'
                    : isActive('/weekly-challenge')
                    ? 'bg-[#0065F8] text-white shadow-[0px_3px_0px_#073B8E]'
                    : 'text-[#334155] hover:text-[#0065F8] hover:bg-[rgba(0,101,248,0.06)]'
                }`}
              >
                <span>Weekly Challenge</span>
              </button>
            </div>
          )}

          {/* RIGHT: User Section (only when authenticated) */}
          {isAuthenticated && (
            <div className={`hidden lg:flex items-center flex-shrink-0 ${showTierBadge ? 'space-x-2' : 'space-x-3'}`}>
                {/* Tier Badge for All Users - Dynamic Island Style */}
                {showTierBadge && (
                  <button
                    onClick={handlePremiumBadgeClick}
                    className="relative group cursor-pointer transition-all duration-300 hover:scale-105"
                  >
                    {isFreeUser ? (
                      <div className="relative px-4 py-2 rounded-full flex items-center gap-2" style={{ background: "#EAF3FF", border: "2px solid rgba(0,101,248,0.12)", boxShadow: "0px 2px 0px rgba(11,26,51,0.06)" }}>
                        <svg width="14" height="14" fill="#0065F8" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                        <span style={{ color: "#0065F8", fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: "0.04em" }}>Upgrade</span>
                      </div>
                    ) : (
                      <div className="relative px-4 py-2 rounded-full flex items-center gap-2" style={{ background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)", boxShadow: "0px 3px 0px #073B8E" }}>
                        <svg width="14" height="14" fill="#FFF" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                        <span style={{ color: "#FFF", fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: "0.04em" }}>{currentTierName}</span>
                      </div>
                    )}
                  </button>
                )}

                {/* Pixley Stars Display */}
                <button
                  onClick={() => ![9, 11, 12].includes(currentTutorialStep) && handleStarChipClick()}
                  disabled={[9, 11, 12].includes(currentTutorialStep)}
                  className={`px-3 py-2 rounded-full border-2 flex items-center gap-1.5 transition-all duration-150 focus:outline-none ${
                    [9, 11, 12].includes(currentTutorialStep)
                      ? 'opacity-30 cursor-not-allowed'
                      : 'cursor-pointer hover:-translate-y-px'
                  }`}
                  style={{ background: "#FFFDF7", borderColor: "rgba(0,101,248,0.12)", boxShadow: "0px 2px 0px rgba(11,26,51,0.06)" }}
                >
                  <img
                    src="/assets/pixley_stars.png"
                    alt="Pixley Stars"
                    className="w-4 h-4"
                  />
                  <span style={{ fontSize: 14, fontWeight: 700, color: starsAreFrozen ? "#8A99B3" : "#0B1A33" }}>
                    {user?.pixley_stars ?? 0}
                  </span>
                  {starsAreFrozen && (
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="#8A99B3" aria-label="Stars locked">
                      <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v2H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-1V6a4 4 0 00-4-4zm2 6V6a2 2 0 10-4 0v2h4z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>

                {/* Profile Menu Dropdown */}
                <div className="relative" ref={profileMenuRef}>
                  <button
                    onClick={() => ![9, 11, 12].includes(currentTutorialStep) && setIsProfileMenuOpen(!isProfileMenuOpen)}
                    disabled={[9, 11, 12].includes(currentTutorialStep)}
                    className={`w-9 h-9 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full flex items-center justify-center shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/20 overflow-hidden ${
                      [9, 11, 12].includes(currentTutorialStep)
                        ? 'opacity-30 cursor-not-allowed'
                        : 'hover:shadow-lg hover:scale-105'
                    }`}
                  >
                    {currentProfile?.avatar_url ? (
                      <img
                        src={currentProfile.avatar_url}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-white font-bold text-sm">
                        {currentProfile?.name?.charAt(0)?.toUpperCase() || user?.name?.charAt(0) || user?.email?.charAt(0) || '?'}
                      </span>
                    )}
                  </button>

                  {/* Dropdown Menu */}
                  {isProfileMenuOpen && (
                    <div
                      className="absolute right-0 mt-3 w-72 overflow-hidden z-50"
                      style={{
                        background: "#FFFFFF",
                        border: "2px solid rgba(11,26,51,0.06)",
                        borderRadius: 20,
                        boxShadow: "0px 5px 0px rgba(11,26,51,0.08), 0px 18px 36px rgba(11,26,51,0.14)",
                        fontFamily: "'Inter', 'Fredoka', sans-serif",
                      }}
                    >
                      {/* Current Profile Header */}
                      {currentProfile && (
                        <div
                          className="px-4 py-3.5"
                          style={{
                            background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
                            borderBottom: "2px solid rgba(11,26,51,0.06)",
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className="flex items-center justify-center overflow-hidden flex-shrink-0"
                              style={{
                                width: 44,
                                height: 44,
                                borderRadius: "50%",
                                background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)",
                                border: "2px solid #FFFFFF",
                                boxShadow: "0px 2px 0px rgba(11,26,51,0.08)",
                              }}
                            >
                              {currentProfile.avatar_url ? (
                                <img src={currentProfile.avatar_url} alt={currentProfile.name} className="w-full h-full object-cover" />
                              ) : (
                                <span style={{ ...FREDOKA, color: "#FFFFFF", fontWeight: 800, fontSize: 16 }}>{currentProfile.name.charAt(0).toUpperCase()}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="truncate" style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 15 }}>{currentProfile.name}</div>
                              <div style={{ ...FREDOKA, color: "#0065F8", fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginTop: 2 }}>Current Profile</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Profile List */}
                      {profiles && profiles.length > 1 && (
                        <>
                          <div className="py-2">
                            <div className="px-4 pt-2 pb-1.5" style={{ ...FREDOKA, color: "#5A6B85", fontSize: 10, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase" }}>Switch Profile</div>
                            {profiles.filter(p => p.id !== currentProfile?.id).map((profile) => (
                              <button
                                key={profile.id}
                                onClick={() => {
                                  requireParentalVerification('switch profiles', () => {
                                    switchProfile(profile.id);
                                    setIsProfileMenuOpen(false);
                                  });
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-left transition-colors"
                                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0,101,248,0.06)")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                              >
                                <div
                                  className="flex items-center justify-center overflow-hidden flex-shrink-0"
                                  style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: "50%",
                                    background: "linear-gradient(135deg, rgba(0,101,248,0.18) 0%, rgba(0,202,255,0.18) 100%)",
                                    border: "2px solid #FFFFFF",
                                    boxShadow: "0px 1px 0px rgba(11,26,51,0.06)",
                                  }}
                                >
                                  {profile.avatar_url ? (
                                    <img src={profile.avatar_url} alt={profile.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <span style={{ ...FREDOKA, color: "#0065F8", fontWeight: 800, fontSize: 14 }}>{profile.name.charAt(0).toUpperCase()}</span>
                                  )}
                                </div>
                                <span className="truncate" style={{ ...FREDOKA, color: "#0B1A33", fontSize: 14, fontWeight: 600 }}>{profile.name}</span>
                              </button>
                            ))}
                          </div>
                          <div style={{ borderTop: "2px solid rgba(11,26,51,0.06)" }} />
                        </>
                      )}

                      {/* Menu Options */}
                      <div className="py-2">
                        <button
                          onClick={() => {
                            handleNavigation('/profiles');
                            setIsProfileMenuOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-left transition-colors"
                          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0,101,248,0.06)")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="#0065F8" strokeWidth="2.2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span style={{ ...FREDOKA, color: "#0B1A33", fontSize: 14, fontWeight: 600 }}>Manage Profiles</span>
                        </button>
                        <button
                          onClick={() => {
                            requireParentalVerification('access settings', () => {
                              handleNavigation('/settings');
                              setIsProfileMenuOpen(false);
                            });
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-left transition-colors"
                          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0,101,248,0.06)")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="#0065F8" strokeWidth="2.2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span style={{ ...FREDOKA, color: "#0B1A33", fontSize: 14, fontWeight: 600 }}>Settings</span>
                        </button>
                      </div>

                      <div style={{ borderTop: "2px solid rgba(11,26,51,0.06)" }} />

                      {/* Sign Out */}
                      <div className="py-2">
                        <button
                          onClick={() => { handleSignOut(); }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-left transition-colors"
                          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(220,38,38,0.06)")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="#DC2626" strokeWidth="2.2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
                          </svg>
                          <span style={{ ...FREDOKA, color: "#DC2626", fontSize: 14, fontWeight: 700 }}>Sign Out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Mobile: Tier badge + Action button + Hamburger grouped together on the right */}
            <div className="lg:hidden flex items-center gap-2">
              {/* Mobile Tier Badge - inline in toolbar */}
              {isAuthenticated && showTierBadge && (
                <button
                  onClick={handlePremiumBadgeClick}
                  className="transition-all duration-200 hover:scale-105"
                >
                  {isFreeUser ? (
                    <div className="px-2.5 py-1 rounded-full flex items-center gap-1" style={{ background: "#EAF3FF", border: "2px solid rgba(0,101,248,0.12)", boxShadow: "0px 2px 0px rgba(11,26,51,0.06)" }}>
                      <svg className="w-3 h-3" fill="#0065F8" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      <span className="font-bold text-[10px] tracking-wide uppercase" style={{ color: "#0065F8" }}>Free</span>
                    </div>
                  ) : (
                    <div className="px-2.5 py-1 rounded-full flex items-center gap-1" style={{ background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)", boxShadow: "0px 2px 0px #073B8E" }}>
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      <span className="text-white font-bold text-[10px] tracking-wide uppercase">{currentTierName}</span>
                    </div>
                  )}
                </button>
              )}

              {/* Mobile Stars Display */}
              {isAuthenticated && (
                <button
                  onClick={() => { handleStarChipClick(); setIsMenuOpen(false); }}
                  className="px-2 py-1 rounded-full flex items-center gap-1 transition-all duration-200 hover:scale-105"
                  style={{ background: "#FFFDF7", border: "2px solid rgba(0,101,248,0.12)", boxShadow: "0px 2px 0px rgba(11,26,51,0.06)" }}
                >
                  <img src="/assets/pixley_stars.png" alt="Stars" className="w-3.5 h-3.5" />
                  <span className="text-[11px] font-bold" style={{ color: "#0B1A33" }}>{user?.pixley_stars ?? 0}</span>
                </button>
              )}

              {isAuthenticated ? (
                <button
                  onClick={() => handleNavigation('/create')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide text-white transition-all duration-150 hover:-translate-y-px"
                  style={{ background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)", boxShadow: "0px 3px 0px #073B8E", letterSpacing: "0.04em" }}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  <span>Create</span>
                </button>
              ) : (
                <button
                  onClick={() => navigate('/auth?mode=login')}
                  className="flex items-center px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide text-white transition-all duration-150 hover:-translate-y-px"
                  style={{ background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)", boxShadow: "0px 3px 0px #073B8E", letterSpacing: "0.04em" }}
                >
                  <span>Log In</span>
                </button>
              )}

              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`p-2 rounded-lg ${textSecondary} ${hoverText} ${hoverBg} transition-all duration-200 focus:outline-none focus:ring-2 ${isAboutPage ? 'focus:ring-black/20' : 'focus:ring-white/20'}`}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {isMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className={`lg:hidden mt-1 py-2 border-t ${borderColor}`}>
            {isAuthenticated ? (
              <>
                <div className="space-y-1 mb-2 px-2">
                  {navItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => handleNavigation(item.path)}
                      className={`px-3 py-2 rounded-xl transition-all duration-150 w-full text-left focus:outline-none ${
                        isActive(item.path)
                          ? 'bg-[#0065F8] text-white shadow-[0px_3px_0px_#073B8E]'
                          : 'text-[#334155] hover:text-[#0065F8] hover:bg-[rgba(0,101,248,0.06)]'
                      }`}
                    >
                      <span className="font-semibold text-sm">{item.label}</span>
                    </button>
                  ))}
                </div>

                {/* Mobile Search Bar - Visible only on Storyverse page when authenticated */}
                {isAuthenticated && isStoriversePage && (
                  <div className="px-2 mb-2">
                    <div
                      className="flex items-center rounded-full px-3 py-2 transition-all duration-150 focus-within:ring-2 focus-within:ring-[#0065F8]/30"
                      style={{ background: "#FFFFFF", border: "2px solid rgba(0,101,248,0.12)", boxShadow: "0px 2px 0px rgba(11,26,51,0.06)" }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={'rgba(0, 0, 0, 0.6)'}
                        strokeWidth="2"
                        className="flex-shrink-0"
                      >
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                      </svg>

                      <input
                        type="text"
                        placeholder="Search stories..."
                        value={localSearchQuery}
                        onChange={(e) => { setLocalSearchQuery(e.target.value); }}
                        onKeyDown={handleSearchKeyDown}
                        className={`flex-1 bg-transparent border-none text-sm ${textPrimary} placeholder-black/50 outline-none px-3`}
                      />

                      {localSearchQuery && (
                        <button
                          onClick={handleClearSearch}
                          className={"text-[#334155]/50 hover:text-[#0065F8] transition-colors duration-150 flex-shrink-0 px-1"}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      )}

                      <button
                        onClick={handleSearchWithNavigation}
                        disabled={!localSearchQuery.trim()}
                        className="bg-[#0065F8] hover:bg-[#0055D6] disabled:bg-[#D7E3F3] disabled:text-[#8CA0BC] disabled:cursor-not-allowed text-white text-xs font-bold px-3 py-1 rounded-full ml-2 transition-all duration-150 flex-shrink-0"
                      >
                        Go
                      </button>
                    </div>
                  </div>
                )}

                {/* Weekly Challenge Button - Mobile */}
                <div className="px-2 mb-2">
                  <button
                    onClick={() => {
                      handleNavigation('/weekly-challenge');
                      setIsMenuOpen(false);
                    }}
                    className={`px-3 py-2 rounded-xl transition-all duration-150 w-full text-left focus:outline-none ${
                      isActive('/weekly-challenge')
                        ? 'bg-[#0065F8] text-white shadow-[0px_3px_0px_#073B8E]'
                        : 'text-[#334155] hover:text-[#0065F8] hover:bg-[rgba(0,101,248,0.06)]'
                    }`}
                  >
                    <span className="font-semibold text-sm">⭐ Weekly Challenge</span>
                  </button>
                </div>

                {/* Games Button - Mobile */}
                <div className="px-2 mb-2">
                  <button
                    onClick={() => {
                      handleNavigation('/games');
                      setIsMenuOpen(false);
                    }}
                    className={`px-3 py-2 rounded-xl transition-all duration-150 w-full text-left focus:outline-none ${
                      isActive('/games')
                        ? 'bg-[#0065F8] text-white shadow-[0px_3px_0px_#073B8E]'
                        : 'text-[#334155] hover:text-[#0065F8] hover:bg-[rgba(0,101,248,0.06)]'
                    }`}
                  >
                    <span className="font-semibold text-sm">Games</span>
                  </button>
                </div>

                {/* Mobile User Section */}
                <div className={`pt-2 border-t ${borderColor} px-2`}>
                  <div className="flex items-center justify-between gap-2">
                    {/* Tier Badge for All Users */}
                    {showTierBadge && (
                      <button
                        onClick={handlePremiumBadgeClick}
                        className="flex-1 flex justify-center"
                      >
                        {isFreeUser ? (
                          <div className="px-3 py-1 rounded-full flex items-center gap-1.5 transition-all duration-200" style={{ background: "#EAF3FF", border: "2px solid rgba(0,101,248,0.12)", boxShadow: "0px 2px 0px rgba(11,26,51,0.06)" }}>
                            <svg className="w-3 h-3" fill="#0065F8" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            <span className="font-bold text-xs tracking-wide uppercase" style={{ color: "#0065F8" }}>Free</span>
                          </div>
                        ) : (
                          <div className="px-3 py-1 rounded-full flex items-center gap-1.5 transition-all duration-200" style={{ background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)", boxShadow: "0px 2px 0px #073B8E" }}>
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            <span className="text-white font-bold text-xs tracking-wide uppercase">{currentTierName}</span>
                          </div>
                        )}
                      </button>
                    )}

                    {/* Pixley Stars Display - Mobile */}
                    <button
                      onClick={() => {
                        handleStarChipClick();
                        setIsMenuOpen(false);
                      }}
                      className="px-2.5 py-1.5 rounded-full flex items-center gap-1 transition-all duration-200 hover:scale-105 cursor-pointer focus:outline-none"
                      style={{ background: "#FFFDF7", border: "2px solid rgba(0,101,248,0.12)", boxShadow: "0px 2px 0px rgba(11,26,51,0.06)" }}
                    >
                      <img
                        src="/assets/pixley_stars.png"
                        alt="Pixley Stars"
                        className="w-3.5 h-3.5"
                      />
                      <span className="text-xs font-bold" style={{ color: "#0B1A33" }}>
                        {user?.pixley_stars ?? 0}
                      </span>
                    </button>

                    {/* Profile Picture */}
                    <button
                      onClick={() => handleNavigation('/settings')}
                      className="w-8 h-8 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full flex items-center justify-center shadow-sm transition-all duration-200 focus:outline-none overflow-hidden"
                    >
                      {user?.profile_picture && !user.profile_picture.startsWith('default:') ? (
                        <img
                          src={user.profile_picture}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-white font-bold text-xs">
                          {user?.profile_picture?.startsWith('default:')
                            ? user.profile_picture.split(':')[1]
                            : user?.name?.charAt(0) || user?.email?.charAt(0) || '?'}
                        </span>
                      )}
                    </button>

                    {/* Sign Out Button */}
                    <button
                      onClick={handleSignOut}
                      className={`px-3 py-1 text-xs ${textSecondary} hover:text-red-400 hover:bg-red-500/10 rounded-full transition-all duration-200 focus:outline-none`}
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="pt-2 px-2 space-y-2">
                <button
                  onClick={() => {
                    navigate('/auth?mode=login');
                    setIsMenuOpen(false);
                  }}
                  className="w-full rounded-full px-4 py-3 text-sm font-bold uppercase tracking-wide text-white transition-all duration-150 focus:outline-none"
                  style={{ background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)", boxShadow: "0px 3px 0px #073B8E", letterSpacing: "0.04em" }}
                >
                  Log In
                </button>
                <button
                  onClick={() => {
                    navigate('/auth?mode=signup');
                    setIsMenuOpen(false);
                  }}
                  className="w-full rounded-full px-4 py-3 text-sm font-bold uppercase tracking-wide transition-all duration-150 focus:outline-none"
                  style={{ background: "#EAF3FF", border: "2px solid rgba(0,101,248,0.18)", boxShadow: "0px 2px 0px rgba(11,26,51,0.06)", color: "#0065F8", letterSpacing: "0.04em" }}
                >
                  Sign Up
                </button>
                <button
                  onClick={() => {
                    handleNavigation('/storyverse');
                    setIsMenuOpen(false);
                  }}
                  className="w-full rounded-full px-4 py-3 text-sm font-bold uppercase tracking-wide transition-all duration-150 focus:outline-none"
                  style={{ background: "#FFFFFF", border: "2px solid rgba(11,26,51,0.08)", boxShadow: "0px 2px 0px rgba(11,26,51,0.06)", color: "#0B1A33", letterSpacing: "0.04em" }}
                >
                  Storyverse
                </button>
                <button
                  onClick={() => {
                    handleNavigation('/blog');
                    setIsMenuOpen(false);
                  }}
                  className="w-full rounded-full px-4 py-3 text-sm font-bold uppercase tracking-wide transition-all duration-150 focus:outline-none"
                  style={{ background: "#FFFFFF", border: "2px solid rgba(11,26,51,0.08)", boxShadow: "0px 2px 0px rgba(11,26,51,0.06)", color: "#0B1A33", letterSpacing: "0.04em" }}
                >
                  Blog
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pixley Stars Modal */}
      <PixleyStarsModal
        isOpen={isStarsModalOpen}
        onClose={() => setIsStarsModalOpen(false)}
      />

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={isSubscriptionModalOpen}
        onClose={handleCloseSubscriptionModal}
        apiClient={apiClient}
        user={user}
      />
    </nav>
  );
};

export default Navigation;
